/**
 * Helpers for refreshing the record cache after a "Migrate deprecations" apply.
 *
 * Regression guard for operator-task-fe-migrate-deprecations-preview-diff-button
 * (Margot review, 2026-09-11): after applying a migration the screen kept showing
 * the "Migrate deprecations" action button and the "has deprecation" badge until a
 * navigate-away-and-back full re-fetch. Both surfaces read `has_deprecation` off
 * the cached record, and the record screen keys its cache by the URL uid
 * (useResourceData) — the id for `?id=…` links, the name for `?name=…` links.
 * Those two slices can coexist (live data subscribes by id while the screen was
 * opened by name), and the old saga refreshed only the id-iri when present, so the
 * name-keyed slice the screen actually rendered stayed stale.
 *
 * These pure helpers isolate the two decisions the saga makes so they can be unit
 * tested without driving the whole generator.
 */

/** Fields the migrate_deprecations endpoints return that are transient action
 * results (not record columns) and must not be persisted onto the cached record. */
const TRANSIENT_MIGRATE_FIELDS = ["errors", "diff"] as const;

/**
 * Strip the transient (non-record) fields from a migrate_deprecations apply
 * response, leaving only the record columns to shallow-merge into the cache
 * (e.g. flow: script/has_deprecation/has_syntax_error; connector: value/
 * has_deprecation). Returns a new object; the input is not mutated.
 */
export const stripTransientMigrateFields = (
    responseData: Record<string, unknown> | null | undefined
): Record<string, unknown> => {
    const patch = { ...(responseData ?? {}) };
    for (const field of TRANSIENT_MIGRATE_FIELDS) {
        delete patch[field];
    }
    return patch;
};

/**
 * Given the id-based and name-based iris and whether each currently has a cache
 * slice, return every iri whose slice should be refreshed. Refreshing *all*
 * existing slices (rather than just the id-iri) guarantees the one the screen
 * renders is updated regardless of which link form opened it.
 */
export const computeMigrateRefreshTargets = ({
    iri,
    iriByName,
    hasIdSlice,
    hasNameSlice,
}: {
    iri: string;
    iriByName: string;
    hasIdSlice: boolean;
    hasNameSlice: boolean;
}): string[] => {
    const targets: string[] = [];
    if (hasIdSlice) {
        targets.push(iri);
    }
    if (hasNameSlice && iriByName !== iri) {
        targets.push(iriByName);
    }
    return targets;
};
